<template>
  <HeaderComponent />
  
  <!-- Breadcrumb -->
  <section class="w-full mt-[70px] bg-gray-50 border-b border-gray-200 py-4">
    <div class="container mx-auto px-6">
      <nav class="flex" aria-label="Breadcrumb">
        <ol class="inline-flex items-center space-x-2 text-sm">
          <li class="inline-flex items-center">
            <router-link to="/" class="text-gray-500 hover:text-orange-600 transition-colors">Accueil</router-link>
          </li>
          <li>
            <div class="flex items-center">
              <svg class="w-4 h-4 text-gray-400 mx-1" fill="currentColor" viewBox="0 0 20 20">
                <path fill-rule="evenodd" d="M7.293 14.707a1 1 0 010-1.414L10.586 10 7.293 6.707a1 1 0 011.414-1.414l4 4a1 1 0 010 1.414l-4 4a1 1 0 01-1.414 0z" clip-rule="evenodd"></path>
              </svg>
              <router-link to="/boutique" class="text-gray-500 hover:text-orange-600 transition-colors">Boutique</router-link>
            </div>
          </li>
          <li>
            <div class="flex items-center">
              <svg class="w-4 h-4 text-gray-400 mx-1" fill="currentColor" viewBox="0 0 20 20">
                <path fill-rule="evenodd" d="M7.293 14.707a1 1 0 010-1.414L10.586 10 7.293 6.707a1 1 0 011.414-1.414l4 4a1 1 0 010 1.414l-4 4a1 1 0 01-1.414 0z" clip-rule="evenodd"></path>
              </svg>
              <span class="text-orange-600 font-medium">{{ produit.name }}</span>
            </div>
          </li>
        </ol>
      </nav>
    </div>
  </section>

  <!-- Notification -->
  <div v-if="ajouterProduit" class="fixed top-20 left-1/2 transform -translate-x-1/2 z-50">
    <div class="bg-green-500 text-white px-6 py-3 rounded-lg shadow-lg flex items-center animate-fade-in">
      <i class="fas fa-check-circle mr-2"></i>
      {{ ajouterProduit }}
    </div>
  </div>

  <!-- Product Detail -->
  <section class="container mx-auto px-6 py-12 relative">
    <!-- Loading Overlay -->
    <div v-if="isLoading" class="absolute inset-0 bg-white bg-opacity-80 z-10 flex items-center justify-center">
      <div class="w-12 h-12 border-4 border-orange-500 border-t-transparent rounded-full animate-spin"></div>
    </div>

    <div class="flex flex-col lg:flex-row gap-12">
      <!-- Product Gallery -->
      <div class="lg:w-1/2">
        <!-- Main Image -->
        <div class="bg-gray-100 rounded-xl overflow-hidden mb-4 h-96 flex items-center justify-center">
          <img 
            :src="selectedImage || defaultImage" 
            :alt="produit.name" 
            class="object-contain w-full h-full p-8 transition-opacity duration-300"
            @error="handleImageError"
          >
        </div>
        
        <!-- Thumbnails -->
        <div v-if="produit.photos && produit.photos.length" class="flex gap-3 overflow-x-auto py-2">
          <div 
            v-for="(image, index) in produit.photos" 
            :key="index" 
            @click="selectedImage = image"
            class="flex-shrink-0 w-20 h-20 bg-gray-100 rounded-lg overflow-hidden cursor-pointer border-2 transition-all"
            :class="{
              'border-orange-500': selectedImage === image,
              'border-transparent': selectedImage !== image,
              'hover:border-orange-300': selectedImage !== image
            }"
          >
            <img 
              :src="image" 
              :alt="'Thumbnail ' + (index + 1)" 
              class="object-cover w-full h-full"
              @error="handleThumbnailError($event, index)"
            >
          </div>
        </div>
      </div>

      <!-- Product Info -->
      <div class="lg:w-1/2">
        <!-- Product Header -->
        <div class="mb-6">
          <span v-if="hasPromo" class="inline-block mb-2 bg-red-600 text-white text-xs font-bold px-3 py-1 rounded-full animate-pulse">
            <i class="fas fa-fire mr-1"></i> PROMO
          </span>
          <h1 class="text-3xl font-bold text-gray-900 mb-2">{{ produit.name }}</h1>
          <div class="flex items-center mb-4">
            <div class="flex text-orange-400 mr-3">
              <i class="fas fa-star"></i>
              <i class="fas fa-star ml-1"></i>
              <i class="fas fa-star ml-1"></i>
              <i class="fas fa-star ml-1"></i>
              <i class="fas fa-star-half-alt ml-1"></i>
            </div>
            <span class="text-gray-400 mx-2">|</span>
            <span class="text-green-600 font-medium flex items-center">
              <i class="fas fa-check-circle mr-1"></i> En stock
            </span>
          </div>
        </div>

        <!-- Product Description -->
        <div class="mb-8">
          <h3 class="text-lg font-semibold text-gray-900 mb-2">Description</h3>
          <p class="text-gray-600">{{ produit.description || 'Aucune description disponible' }}</p>
        </div>

        <!-- Variant Selection -->
        <div v-if="distinctPrices.length" class="mb-8">
          <h3 class="text-lg font-semibold text-gray-900 mb-3">Options disponibles</h3>
          <div class="space-y-3">
            <div 
              v-for="priceDetail in distinctPrices" 
              :key="priceDetail.price"
              @click="selectProduct(priceDetail)"
              :class="{
                'ring-2 ring-orange-500 bg-orange-50': selectedProduct === priceDetail,
                'bg-gray-50 hover:bg-gray-100': selectedProduct !== priceDetail
              }"
              class="p-4 rounded-lg cursor-pointer transition-all"
            >
              <div class="flex justify-between items-center">
                <div>
                  <p class="font-bold text-gray-900 text-lg">{{ formatPrice(priceDetail.price) }}</p>
                  <div v-if="priceDetail.originalPrice !== priceDetail.price" class="flex items-center">
                    <span class="text-gray-400 text-sm line-through mr-2">{{ formatPrice(priceDetail.originalPrice) }}</span>
                    <span class="bg-orange-100 text-orange-800 text-xs px-2 py-1 rounded">
                      Économisez {{ formatPrice(priceDetail.originalPrice - priceDetail.price) }}
                    </span>
                  </div>
                </div>
                <div v-if="priceDetail.capacity || priceDetail.color" class="text-right">
                  <p v-if="priceDetail.capacity" class="text-gray-600 text-sm">{{ priceDetail.capacity }}</p>
                  <p v-if="priceDetail.color" class="text-gray-600 text-sm">{{ priceDetail.color }}</p>
                </div>
              </div>
            </div>
          </div>
        </div>

        <!-- Action Buttons -->
        <div class="sticky bottom-0 bg-white py-4 lg:py-0 lg:mt-8 border-t lg:border-t-0">
          <div class="flex flex-col sm:flex-row gap-4">
            <button 
              @click="addToCart"
              class="flex-1 py-3 px-6 bg-orange-500 hover:bg-orange-600 text-white font-bold rounded-lg transition-colors flex items-center justify-center"
            >
              <i class="fas fa-shopping-cart mr-2"></i> Ajouter au panier
            </button>
            <button
              @click="payNow"
              class="flex-1 py-3 px-6 bg-gray-900 hover:bg-gray-800 text-white font-bold rounded-lg transition-colors flex items-center justify-center"
            >
              <i class="fas fa-bolt mr-2"></i> Acheter maintenant
            </button>
          </div>
          <button
            v-if="isPhone"
            @click="payParTranches"
            :disabled="isCreatingPlan"
            class="w-full mt-4 py-3 px-6 border-2 border-orange-500 text-orange-600 hover:bg-orange-50 font-bold rounded-lg transition-colors flex items-center justify-center"
          >
            <i class="fas fa-hand-holding-dollar mr-2"></i>
            <span v-if="!isCreatingPlan">Payer par tranches</span>
            <span v-else>Création en cours...</span>
          </button>
        </div>
      </div>
    </div>
  </section>

  <!-- Modale choix boutique (paiement par tranches) -->
  <div v-if="showBoutiqueModal" class="fixed inset-0 bg-black/50 z-[1000000] flex items-center justify-center p-4" @click.self="showBoutiqueModal = false">
    <div class="bg-white rounded-xl shadow-2xl w-full max-w-sm p-6">
      <h3 class="text-lg font-bold text-gray-800 mb-1">Boutique de retrait</h3>
      <p class="text-sm text-gray-500 mb-4">Où récupérerez-vous le téléphone une fois soldé ?</p>
      <div class="flex flex-col gap-2 mb-4">
        <button v-for="b in boutiques" :key="b" @click="boutiqueChoisie = b" :class="boutiqueChoisie === b ? 'border-orange-500 bg-orange-50 text-orange-700' : 'border-gray-200 text-gray-700'" class="border-2 rounded-lg py-3 font-semibold transition-colors">{{ b }}</button>
      </div>
      <button @click="confirmPlan" :disabled="!boutiqueChoisie || isCreatingPlan" class="w-full py-3 rounded-lg bg-orange-600 text-white font-bold hover:bg-orange-700 disabled:opacity-50">
        <span v-if="!isCreatingPlan">Confirmer</span><span v-else>Création...</span>
      </button>
    </div>
  </div>

  <FooterComponent />
</template>

<script setup>
import HeaderComponent from '../components/HeaderComponent.vue'
import { ref, computed, onMounted } from 'vue'
import { useRouter } from 'vue-router'
import axios from "axios"
import { useCartStore } from '../store/useCartStore.ts'
import { useAuthClientStore } from '../store/useAuthClientStore.ts'

const router = useRouter()
const authClientStore = useAuthClientStore()
const isCreatingPlan = ref(false)
const produit = ref({})
const isLoading = ref(true)
const selectedImage = ref('')
const selectedProduct = ref(null)
const ajouterProduit = ref('')
const distinctPrices = ref([])
const category = ref('')
const defaultImage = '/img/default.png'
const brokenImages = ref([]) // Pour suivre les images cassées

const produitId = router.currentRoute.value.params.id;
const cartStore = useCartStore()

const fetchProduitById = async () => {
  try {
    const response = await axios.get(`/api/lots/${produitId}`);
    produit.value = response.data;
    
    // Filtrer les images cassées dès le chargement
    if (produit.value.photos && produit.value.photos.length) {
      selectedImage.value = produit.value.photos[0];
    } else {
      selectedImage.value = defaultImage;
    }
    
    category.value = response.data.categorie
    const productDetails = produit.value.produits
      .filter(prod => prod.isOnline)
      .map(prod => ({
        id: prod._id,
        imei1: prod.imei1,
        imei2: prod.imei2,
        price: prod.prix_reduit || prod.prix,
        originalPrice: prod.prix,
        capacity: prod.capacities?.[0] || null,
        color: prod.colors?.[0] || null,
        isPromo: !!prod.isPromo,
      }));

    distinctPrices.value = [...new Map(productDetails.map(item => [item.price, item])).values()];

    if (distinctPrices.value.length > 0) {
      selectedProduct.value = distinctPrices.value[0];
    }
  } catch (err) {
    console.error('Erreur lors du chargement du produit:', err);
  } finally {
    isLoading.value = false;
  }
};

// Gestion des erreurs d'image
const handleImageError = (event) => {
  event.target.src = defaultImage;
  if (produit.value.photos && produit.value.photos.includes(event.target.src)) {
    brokenImages.value.push(event.target.src);
    tryNextImage();
  }
};

// Gestion des erreurs de miniature
const handleThumbnailError = (event, index) => {
  event.target.src = defaultImage;
  brokenImages.value.push(produit.value.photos[index]);
};

// Essayer l'image suivante si l'actuelle est cassée
const tryNextImage = () => {
  if (!produit.value.photos) return;
  
  const currentIndex = produit.value.photos.indexOf(selectedImage.value);
  if (currentIndex === -1) return;
  
  const nextIndex = (currentIndex + 1) % produit.value.photos.length;
  if (!brokenImages.value.includes(produit.value.photos[nextIndex])) {
    selectedImage.value = produit.value.photos[nextIndex];
  }
};

const selectProduct = (productDetail) => {
  selectedProduct.value = productDetail;
}

const hideajouterProduitAfterDelay = () => {
  setTimeout(() => {
    ajouterProduit.value = ''; 
  }, 3000);
};

const formatPrice = (price) => {
  return new Intl.NumberFormat('fr-FR').format(price) + ' FCFA';
};

const addToCart = () => {
  if (!selectedProduct.value) return;

  const productToAdd = {
    id: produit.value._id,
    productId: selectedProduct.value.id,
    imei1: selectedProduct.value.imei1,
    imei2: selectedProduct.value.imei2,
    name: produit.value.name,
    price: selectedProduct.value.price,
    category: category.value,
    originalPrice: selectedProduct.value.originalPrice,
    image: selectedImage.value,
    capacity: selectedProduct.value.capacity,
    color: selectedProduct.value.color,
    quantity: 1,
  }
  
  cartStore.addToCart(productToAdd)
  ajouterProduit.value = 'Produit ajouté au panier'
  hideajouterProduitAfterDelay();
};

const payNow = () => {
  addToCart();
  router.push('/panier');
}

const isPhone = computed(() => ['iOS', 'Android'].includes(category.value));
const hasPromo = computed(() => distinctPrices.value.some(p => p.isPromo));

const boutiques = ['Calavi', 'Parakou'];
const showBoutiqueModal = ref(false);
const boutiqueChoisie = ref('');

const payParTranches = () => {
  if (!selectedProduct.value) return;
  // Connexion requise
  if (!authClientStore.token) {
    router.push({ name: 'connexion', query: { redirect: router.currentRoute.value.fullPath } });
    return;
  }
  boutiqueChoisie.value = '';
  showBoutiqueModal.value = true;
};

const confirmPlan = async () => {
  if (!boutiqueChoisie.value) return;
  isCreatingPlan.value = true;
  try {
    const produitSnapshot = {
      lotId: produit.value._id,
      produitId: selectedProduct.value.id,
      nom: produit.value.name,
      categorie: category.value,
      photo: selectedImage.value,
      caracteristiques: produit.value.description || '',
      capacites: selectedProduct.value.capacity ? [selectedProduct.value.capacity] : [],
      couleurs: selectedProduct.value.color ? [selectedProduct.value.color] : [],
      imei1: selectedProduct.value.imei1,
    };
    await axios.post('/api/plans', {
      origine: 'catalogue',
      produit: produitSnapshot,
      prix: selectedProduct.value.price,
      boutique: boutiqueChoisie.value,
    });
    showBoutiqueModal.value = false;
    ajouterProduit.value = 'Plan de paiement créé ! Rendez-vous dans votre espace.';
    hideajouterProduitAfterDelay();
    setTimeout(() => router.push('/mon-espace'), 900);
  } catch (err) {
    ajouterProduit.value = err.response?.data?.error || 'Erreur lors de la création du plan';
    hideajouterProduitAfterDelay();
  } finally {
    isCreatingPlan.value = false;
  }
};

onMounted(fetchProduitById);
</script>

<style scoped>
.animate-fade-in {
  animation: fadeIn 0.3s ease-in-out;
}

@keyframes fadeIn {
  from { opacity: 0; transform: translateY(-10px); }
  to { opacity: 1; transform: translateY(0); }
}

.animate-spin {
  animation: spin 1s linear infinite;
}

@keyframes spin {
  to { transform: rotate(360deg); }
}
</style>